/**
 * Publish / listen for host moderation notices on LiveKit data channel.
 */

import { useCallback, useEffect, useState } from "react";
import { useMaybeRoomContext } from "@livekit/components-react";
import { RoomEvent } from "livekit-client";
import {
  HOST_MODERATION_TYPE,
  type HostModerationMessage,
} from "./hostModerationTypes";

function encode(msg: object): Uint8Array {
  return new TextEncoder().encode(JSON.stringify(msg));
}

function decode(data: Uint8Array): Record<string, unknown> | null {
  try {
    return JSON.parse(new TextDecoder().decode(data)) as Record<string, unknown>;
  } catch {
    return null;
  }
}

export function useHostModerationSync(options: {
  role: "trainer" | "student";
  localIdentity?: string;
  enabled?: boolean;
  onHostAction?: (msg: HostModerationMessage) => void;
}) {
  const room = useMaybeRoomContext();
  const [lastNotice, setLastNotice] = useState<HostModerationMessage | null>(
    null,
  );

  useEffect(() => {
    // ---------------------------------------------------------------
    // step 1: Students listen for host_moderation messages aimed at them
    // ---------------------------------------------------------------
    if (!options.enabled || !room) return;

    const onData = (payload: Uint8Array) => {
      const raw = decode(payload);
      if (!raw || raw.v !== 1 || raw.type !== HOST_MODERATION_TYPE) return;
      const msg = raw as unknown as HostModerationMessage;
      if (
        options.role === "student" &&
        options.localIdentity &&
        msg.identity !== "*" &&
        msg.identity !== options.localIdentity
      ) {
        return;
      }
      setLastNotice(msg);
      options.onHostAction?.(msg);
    };

    room.on(RoomEvent.DataReceived, onData);
    return () => {
      room.off(RoomEvent.DataReceived, onData);
    };
  }, [
    options.enabled,
    options.localIdentity,
    options.onHostAction,
    options.role,
    room,
  ]);

  const publishHostAction = useCallback(
    async (msg: Omit<HostModerationMessage, "v" | "type" | "at">) => {
      // ---------------------------------------------------------------
      // step 2: Trainer broadcasts soft notice after REST moderation
      // ---------------------------------------------------------------
      if (!room?.localParticipant) return;
      const full: HostModerationMessage = {
        v: 1,
        type: HOST_MODERATION_TYPE,
        at: Date.now(),
        ...msg,
      };
      await room.localParticipant.publishData(encode(full), { reliable: true });
    },
    [room],
  );

  return { lastNotice, publishHostAction };
}
